import type { ReactNode } from 'react';
import type { EtiquetaDeReputacion, Reputacion, TurnoDeAgenda } from '@medchoice/shared';
import { usePedido } from '../../api/usePedido';
import { consultorio } from '../../api/consultorio';
import { Enlace } from '../../router';
import { AvisoError, Icono, Insignia, type NombreIcono, type TonoInsignia } from '../../ui';

const SELLO: Record<EtiquetaDeReputacion, { tono: TonoInsignia; icono: NombreIcono }> = {
  'siempre asiste': { tono: 'confirmado', icono: 'check-circle' },
  'alguna falta': { tono: 'pendiente', icono: 'alert-circle' },
  'falta seguido': { tono: 'ausente', icono: 'user-x' },
  'sin historial': { tono: 'neutral', icono: 'user' },
};

/** Cómo viene cumpliendo el paciente, para ver de un vistazo con quién se compromete. */
export function SelloPaciente({ reputacion }: { reputacion: Reputacion }) {
  const { tono, icono } = SELLO[reputacion.etiqueta];
  return <Insignia tono={tono} tamano="chico" icono={icono}>{reputacion.etiqueta}</Insignia>;
}

export const nombreDe = (p: { nombre: string; apellido: string }) => `${p.nombre} ${p.apellido}`;

export const estaCerrado = (t: Pick<TurnoDeAgenda, 'estado'>) => t.estado === 'atendido' || t.estado === 'ausente';

export function Fila({ icono, etiqueta, children }: { icono: NombreIcono; etiqueta: string; children: ReactNode }) {
  return (
    <div className="fila-dato">
      <Icono nombre={icono} tamano={16} />
      <dt>{etiqueta}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function Seccion({ titulo, descripcion, children }: { titulo: string; descripcion?: ReactNode; children: ReactNode }) {
  return (
    <section className="seccion-config">
      <header>
        <h2>{titulo}</h2>
        {descripcion && <p>{descripcion}</p>}
      </header>
      {children}
    </section>
  );
}

/** Mientras la matrícula no está verificada, el perfil no aparece en la búsqueda. */
export function AvisoDeVerificacion() {
  const { datos } = usePedido(() => consultorio.perfil(), []);
  if (!datos || datos.estado_verificacion === 'verificado') return null;
  return datos.estado_verificacion === 'pendiente' ? (
    <AvisoError tono="aviso">
      Tu matrícula está en revisión. Mientras tanto podés cargar tu perfil, servicios y horarios; los pacientes te van a
      encontrar apenas la administración la verifique.
    </AvisoError>
  ) : (
    <AvisoError>
      No pudimos verificar tu matrícula y tu perfil no aparece en la búsqueda.
      {datos.motivo_rechazo && <> Motivo: {datos.motivo_rechazo.replace(/\.$/, '')}.</>} Revisá los datos en{' '}
      <Enlace a="/pro/perfil">tu perfil</Enlace> y escribinos a soporte@medchoice.com.ar.
    </AvisoError>
  );
}
